import { Model } from 'app/shared/model-system/model';
import { extend, get, intersectionWith, isEmpty, isNil, omit, set } from 'lodash';
import { concat, forkJoin, Observable, of, throwError, timer } from 'rxjs';
import {
  catchError,
  defaultIfEmpty,
  delayWhen,
  map,
  retryWhen,
  switchMap,
  take
} from 'rxjs/operators';
import { ApiService } from '../services/api.service';
import { IPropertyOptions } from './property';

export const WAIT_FOR_SEARCH_DELAY = 500;
export const WAIT_FOR_SEARCH_RETRIES = 9;

/** ? */
export abstract class ModelService<T extends Model> {
  public api: ApiService;

  // eslint-disable-next-line @typescript-eslint/naming-convention
  public abstract Type: any;

  /**
   * Create a new model of the type defined by this service
   *
   * @param projection: string[] - A list of property keys to deserialize
   */
  public create(data: any = null, projection: string[] = null): T {
    if (data instanceof this.Type) {
      return data;
    }

    return this.createModel(this.Type, data, projection);
  }

  /**
   * Update the model with new data, overrides model's raw data with the data provided
   *
   * @param projection: string[] - A list of property keys to deserialize
   */
  public update(model: any, data: any = null, projection: string[] = null): T {
    return this.deserialize(model, data, projection);
  }

  /**
   * Execute the load function on the properties which define one. Uses the projection array to limit
   * the properties that are executed this way.
   *
   * Property's load function MUST return an Observable.
   *
   * @param model: T
   * @param projection: string[] - A list of property keys to execute the load function on
   * @returns Observable<T>
   */
  public loadProperties(model: T, projection: string[] = null): Observable<T> {
    const error = {};

    // Create the pipe functionality for each Observable returned from the child model's save function
    const childModelObservables: Observable<any>[] = this.determineKeys(
      model,
      'load',
      projection
    ).map((key) => {
      debug('model', `- loading property [${key}]`);
      const propertyAttributes = <IPropertyOptions>(
        (<typeof Model>model.constructor).attributes[key]
      );

      return propertyAttributes.load({ model, service: this }).pipe(
        map((ret) => {
          set<any>(model._values, key, ret); // Set the value to the model
          model.unmarkModified(key);
        }),
        catchError((errorValue) => {
          debug('model', `- loading property errored [${key}]`);
          error[key] = errorValue;

          return of(null);
        })
      );
    });

    if (!isEmpty(error)) {
      throwError(error);
    }

    return forkJoin(childModelObservables).pipe(
      defaultIfEmpty(null), // Provide a value to emit if childModelObservables is empty
      switchMap(() => of(model))
    );
  }

  /**
   * Execute the save function on the properties which define one. Uses the projection array to limit
   * the properties that are executed this way.
   *
   * Property's save function MUST return an Observable.
   *
   * @param model: T
   * @param projection: string[] - A list of property keys to execute the save function on
   * @returns Observable<T>
   */
  public saveProperties(model: T, projection: string[] = null): Observable<T> {
    const error = {};

    // Create the pipe functionality for each Observable returned from the child model's save function
    const childModelObservables: Observable<any>[] = this.determineKeys(
      model,
      'save',
      projection
    ).map((key) => {
      debug('model', `- saving property [${key}]`);
      const propertyAttributes = <IPropertyOptions>(
        (<typeof Model>model.constructor).attributes[key]
      );

      return propertyAttributes.save({ model, service: this }).pipe(
        map((ret) => {
          model.unmarkModified(key);
        }),
        catchError((errorValue) => {
          debug('model', `- saving property errored [${key}]`);
          error[key] = errorValue;

          return of(null);
        })
      );
    });

    if (!isEmpty(error)) {
      throwError(error);
    }

    return forkJoin(childModelObservables).pipe(
      defaultIfEmpty(null), // Provide a value to emit if childModelObservables is empty
      switchMap(() => of(model))
    );
  }

  /**
   * Serialize the Model to a format compatible with the the API
   *
   * @param model - The model to serialize
   * @param excludeUnmodified - Exclude any unmodified properties (less leftovers) from the serialized result
   */
  public serialize(model: T, excludeUnmodified: boolean = false): any {
    // convert model data to API format using model attribute serializers if available
    debug('model', '=> Serializing Model', model.toObject());

    let serializedModel = !model._rawApiData || excludeUnmodified ? {} : model._rawApiData;

    // Append the leftovers to the serialized model first, therefore properties with the same name will overwrite the leftovers in the
    // following steps
    extend(serializedModel, this.serializeLeftovers(model));

    // serialize properties that are required or modified. Use a default serializer if one isn't provided
    for (const key of Object.keys(
      omit((<typeof Model>model.constructor).attributes, ['leftovers'])
    )) {
      const propertyAttributes = <IPropertyOptions>(
        (<typeof Model>model.constructor).attributes[key]
      );
      const propertyValue = model._values[key];
      const modelClass = get(propertyAttributes, 'model.class');

      /**
       * Determine if the property should be skipped
       */
      if (isNil(propertyValue) || (!model.isModified(key) && excludeUnmodified)) {
        continue;
      }

      // Property overrides default serializer
      const propertyHasSerializer = typeof propertyAttributes.serialize === 'function';
      // The value of the property is a model, or model-like
      const isChildModel = propertyValue != null && modelClass && !Array.isArray(modelClass);
      // The value of the property is an array of models
      const isArrayOfChildModels =
        Array.isArray(propertyValue) && propertyValue.length > 0 && Array.isArray(modelClass);

      if (propertyHasSerializer) {
        propertyAttributes.serialize({ body: serializedModel, model });
      } else if (isChildModel) {
        serializedModel[key] = this.serialize(propertyValue);
      } else if (isArrayOfChildModels) {
        serializedModel[key] = propertyValue.map((childModel) => this.serialize(childModel));
      } else if (!isNil(propertyValue)) {
        serializedModel[key] = propertyValue;
      }
      debug('model', `- serializing [${key}]`);
    }

    debug('model', '=> Resulting serialized model:', serializedModel);

    return serializedModel;
  }

  /**
   * This polls the API until the returned data matches the
   *    data we sent to the server to save, on a half second timeout
   *    and a max of 10 attempts
   *
   *    @param models: Model[] | any[] - model just saved
   *    @param url: String - search url
   *    @param params: any - params to pass to search url (e.g. { search: model.name })
   */
  public waitForSearch(models: Model[] | any[], url: string, params?: any): Observable<boolean> {
    return this.api.get(url, params).pipe(
      switchMap((data) => this.compareSearchResultToSavedObjects(data, models)),
      retryWhen((errors) =>
        this.waitForXRetry(errors, 'Unable to find object in search after 5 second wait')
      ),
      // Waiting for results shouldn't error, if the API is down. Catch the error and return false.
      catchError((error) => {
        console.error(error);

        return of(false);
      })
    );
  }

  /**
   * Function used for record comparison between data
   *   returned on save, and data returned on query
   *   Overwrite this in your model-service if you need to,
   *   just make sure to return an Observable!
   *
   *   This built-in version compares model.version for one model
   *   see did-pool.service for full Object compare example
   *
   *   @param queryObj: Object - entire object returned by query
   *   @param localObjs: Model[] - model.toObject in memory
   */
  public compareSearchResultToSavedObjects(queryObj: any, localObjs: Model[]): Observable<boolean> {
    let localObj = localObjs[0].toObject();
    const target = queryObj.entities.find((q) => q.id === localObj.id);
    if (!target) {
      return throwError('none found');
    }

    return target.version === localObj.version ? of(true) : throwError('mismatched versions');
  }

  /**
   *    This polls the API until the record is no longer present
   *    in search results, on a half second timeout
   *    and a max of 10 attempts
   *
   *    @param models: Model[] | any[] - models just deleted
   *    @param url: String - search url
   *    @param params: any - params to pass to search url (e.g. { search: model.name })
   */
  public waitForDelete(models: Model[] | any[], url: string, params?: any): Observable<boolean> {
    return this.api.get(url, params).pipe(
      switchMap((data) => this.waitForObjectsToBeDeleted(data, models)),
      retryWhen((errors) =>
        this.waitForXRetry(errors, 'Search contains deleted objects after 5 second wait')
      ),
      // Waiting for results shouldn't error, if the API is down. Catch the error and return false.
      catchError((error) => {
        console.error(error);

        return of(false);
      })
    );
  }

  /**
   *   Function used to check that records were deleted.
   *   Overwrite this in your model-service if you need to,
   *   just make sure to return an Observable!
   *
   *   @param queryObj: Object - entire object returned by query
   *   @param deletedModels: Array - models that were deleted
   */
  public waitForObjectsToBeDeleted(queryObj: any, deletedModels: Model[]): Observable<boolean> {
    const target = intersectionWith(
      queryObj.entities,
      deletedModels,
      (a: any, b: any) => a.id === b.toObject().id
    );
    if (target.length > 0) {
      return throwError('none found');
    }

    return of(true);
  }

  /**
   * Convert API data to model using model attribute deserializers if available
   *
   * @param model - The model to deserialize into
   * @param raw A single record in raw JSON coming from the API
   */
  protected deserialize(model: any, raw: any, projection: string[]): T {
    debug('model', '=> Deserializing API Data', raw);

    // Save for later deserialization, without an extend / clone deep setting values on the model will override the raw data
    model._rawApiData = extend({}, raw);

    let deferred = []; // Used to aggregate child properties which will need to be resolved with branching API calls as needed
    const processed = {};

    const modelKeys =
      projection || Object.keys(omit((<typeof Model>model.constructor).attributes, ['leftovers']));

    modelKeys.forEach((key) => this.processKey(model, deferred, processed, key));

    this.deserializeLeftovers(model, raw);
    model.clearChanges();

    return model;
  }

  /**
   * Create a model of the provided type
   */
  private createModel(modelType: any, data: any, projection: string[] = null): T {
    const childModel = new modelType();

    return this.deserialize(childModel, data, projection);
  }

  /**
   * Deserialize a key based on the Property Options defined
   */
  private processKey(model: T, deferred: any, processed: any, key: string): void {
    // eslint-disable-next-line no-template-curly-in-string
    let requires = get(model.constructor, 'attributes.${key}.requires');
    debug('model', `- deserializing [${key}]`);

    // If the subclass's attribute definition for this key
    if (requires && !processed[requires]) {
      debug('model', 'Deferring Key', key);
      deferred.push(key);

      return;
    }

    const propertyAttributes = <IPropertyOptions>(<typeof Model>model.constructor).attributes[key];
    const modelClass = get(propertyAttributes, 'model.class');

    // Property overrides default serializer
    const propertyHasDeserializer = typeof propertyAttributes.deserialize === 'function';
    // The value of the property is a model, or model-like
    const isChildModel = modelClass && !Array.isArray(modelClass);
    // The value of the property is an array of models
    const isArrayOfChildModels = modelClass && Array.isArray(modelClass);
    // Has defaulting behavior
    const propertyHasDefault = typeof propertyAttributes.default === 'function';

    // If the property has a deserializer use that to set the value of the property. Otherwise use the matching value with
    // the matching key from the raw data.
    model._values[key] = propertyHasDeserializer
      ? propertyAttributes.deserialize({ raw: model._rawApiData, model })
      : model._rawApiData[key];

    // If the value remains undefined and the property has a default function,  use that to set the value
    if (!model._values[key] && propertyHasDefault) {
      model._values[key] = propertyAttributes.default();
    }

    // Mark the key as processed
    processed[key] = true;

    if (!model._values[key]) {
      return;
    }

    const modelProjection = get(propertyAttributes, 'model.projection', null);
    // After the value is determined, check if it is Model-like. If so, create the child model for the data
    if (isChildModel) {
      debug('model', `- deserializing child model [${key}]`);
      model._values[key] = this.createModel(modelClass, model._values[key], modelProjection);
    } else if (isArrayOfChildModels) {
      debug('model', `- deserializing child model array [${key}]`);
      model._values[key] = model._rawApiData[key].map((childData) =>
        this.createModel(modelClass[0], childData, modelProjection)
      );
    }
  }

  /**
   * Provide an object that contains any properties on the raw data that do not share a name with a property on the model
   *
   * @param raw - The raw data
   */
  private deserializeLeftovers(model: any, raw: any): void {
    debug('model', `- deserializing leftovers`);
    const leftoversAttributes = get(model.constructor, 'attributes.leftovers');
    const modelKeys = Object.keys(get(model.constructor, 'attributes'));

    if (!leftoversAttributes) {
      return;
    }

    const leftoverData = omit(raw, modelKeys);
    const leftoversClass = get(leftoversAttributes, 'model.class');

    // Determine if the leftover data should be caste into a Model
    if (leftoversClass && !Array.isArray(leftoversClass)) {
      debug('model', `- deserializing typed leftovers`);
      const typedLeftovers = {};

      // Create a child model of the defined type for each key
      Object.keys(leftoverData).map(
        (key) =>
          // Construct the child model for the key and assign it to typedLeftovers
          (typedLeftovers[key] = this.createModel(leftoversClass, leftoverData[key], null))
      );

      model.leftovers = typedLeftovers;
    } else {
      model.leftovers = leftoverData;
    }
  }

  /**
   * Return a serialized version of the leftover data, if leftovers has a type run each key through the serialize
   * function
   */
  private serializeLeftovers(model: T): any {
    debug('model', `- serializing leftovers`);
    const leftoversAttributes = get(model.constructor, 'attributes.leftovers');
    const leftovers = get(model, 'leftovers', undefined);
    const leftoversClass = get(leftoversAttributes, 'model.class');

    let returnValue;
    if (leftoversClass && !Array.isArray(leftoversClass)) {
      debug('model', `- serializing typed leftovers`);
      returnValue = {};

      for (const key of Object.keys(leftovers)) {
        returnValue[key] = this.serialize(leftovers[key]);
      }
    } else {
      returnValue = leftovers || {};
    }

    return returnValue;
  }

  /**
   * Retry when function for waitForSearch and waitForDelete
   */
  private waitForXRetry(errors: any, errorMessage: string): Observable<any> {
    const retry = errors.pipe(
      delayWhen(() => timer(WAIT_FOR_SEARCH_DELAY)),
      take(WAIT_FOR_SEARCH_RETRIES)
    );

    // The observable completes after 10 tries without firing any events if search doesn't update. Concat an error on the
    // end so we can emit something.
    const didNotFindError = throwError(errorMessage);

    return concat(retry, didNotFindError);
  }

  /**
   * Determine which keys to execute on
   */
  private determineKeys(model: T, functionName: string, projection: string[]): string[] {
    let childModelKeys = projection || Object.keys(get(model, 'constructor.attributes', {}));

    // Filter to the set of keys which have a save function defined and have a valid value for that key
    return childModelKeys.filter(
      (key) =>
        typeof get(model, `constructor.attributes.${key}.${functionName}`) === 'function' &&
        (model._values[key] || model._values[key] === undefined)
    );
  }

  public abstract get(id: string): Promise<T> | Observable<T>;
}
